import * as React from "react";
import { cn } from "@/shared/utils/cn";

interface ToggleGroupProps {
  type: "single";
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

const ToggleGroup = React.forwardRef<HTMLDivElement, ToggleGroupProps>(
  ({ className, value, onValueChange, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("flex items-center justify-center gap-0", className)} {...props}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement<ToggleGroupItemProps>(child)) {
            return React.cloneElement(child, {
              isSelected: child.props.value === value,
              onClick: () => onValueChange?.(child.props.value),
            });
          }
          return child;
        })}
      </div>
    );
  },
);

ToggleGroup.displayName = "ToggleGroup";

interface ToggleGroupItemProps {
  value: string;
  "aria-label"?: string;
  className?: string;
  children: React.ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
}

const ToggleGroupItem = React.forwardRef<HTMLButtonElement, ToggleGroupItemProps>(
  ({ className, children, isSelected, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      data-state={isSelected ? "on" : "off"}
      className={cn(
        "inline-flex items-center justify-center text-sm font-medium transition-colors rounded-sm",
        "hover:bg-stone-50 hover:text-stone-900",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stone-950",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);

ToggleGroupItem.displayName = "ToggleGroupItem";

export { ToggleGroup, ToggleGroupItem };
